import * as React from 'react';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { getUnionTournamentListSummary } from 'Src/helpers/service/adminSU/tournamentsTable';
import { getTournamentCommunicationsInbox } from 'Src/helpers/service/adminSU/tournamentCommunications';
import './Tournaments.css';

type HeaderSection = 'upcoming' | 'archive' | 'invites' | 'communications';

interface Props {
  user: AppUser;
  history: any;
  activeSection: HeaderSection;
  onAddTournament: () => void;
}

interface State {
  upcomingCount?: number;
  archiveCount?: number;
  invitesCount?: number;
  unansweredConfirmationCount?: number;
  nextStart?: string;
  nextTournamentId?: string;
  communicationsUnreadCount?: number;
}

export class UnionTournamentsListHeader extends React.PureComponent<Props, State> {
  state: State = {};

  componentDidMount() {
    getUnionTournamentListSummary(this.props.user)
      .then(summary =>
        this.setState({
          upcomingCount: Number(summary.upcomingCount || 0),
          archiveCount: Number(summary.archiveCount || 0),
          invitesCount: Number(summary.invitesCount || 0),
          unansweredConfirmationCount: Number(summary.unansweredConfirmationCount || 0),
          nextStart:
            summary.nextTournament && summary.nextTournament.startTime
              ? moment(summary.nextTournament.startTime).format('DD MMM')
              : undefined,
          nextTournamentId: summary.nextTournament ? summary.nextTournament.id : undefined
        })
      )
      .catch(() => undefined);
    getTournamentCommunicationsInbox(this.props.user, { unread: true, limit: 100 })
      .then(items => this.setState({ communicationsUnreadCount: items.filter(item => item.unread !== false).length }))
      .catch(() => undefined);
  }

  render() {
    const { activeSection, history, onAddTournament } = this.props;
    const {
      upcomingCount,
      archiveCount,
      invitesCount,
      unansweredConfirmationCount,
      nextStart,
      nextTournamentId,
      communicationsUnreadCount
    } = this.state;
    const openNextTournament = () => {
      if (!nextTournamentId) return;
      const currentLocation = history.location || {};
      history.push({
        pathname: '/tournaments/manage',
        search: `tournament=${nextTournamentId}`,
        state: {
          search: currentLocation.search || '',
          prevPath: currentLocation.pathname || '/tournaments/upcoming'
        }
      });
    };
    const confirmationFilterActive =
      activeSection === 'upcoming' &&
      String((history.location && history.location.search) || '').indexOf('confirmationReplies=unanswered') !== -1;
    const toggleConfirmationFilter = () => {
      history.push({
        pathname: '/tournaments/upcoming',
        search: confirmationFilterActive ? '' : '?confirmationReplies=unanswered'
      });
    };
    const nextStartChip = nextStart ? (
      <span
        className="unionTournamentsNextStart is-clickable"
        role="button"
        tabIndex={0}
        onClick={openNextTournament}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openNextTournament();
          }
        }}
        title="Manage next tournament"
      >
        <b>{nextStart}</b> next start
      </span>
    ) : null;
    const confirmationChip = unansweredConfirmationCount ? (
      <span
        className={`unionTournamentsConfirmationAlert is-clickable${confirmationFilterActive ? ' is-active' : ''}`}
        role="button"
        tabIndex={0}
        onClick={toggleConfirmationFilter}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleConfirmationFilter();
          }
        }}
        title={
          confirmationFilterActive
            ? 'Clear confirmation replies filter'
            : 'Show tournaments needing confirmation replies'
        }
      >
        <b>{unansweredConfirmationCount}</b>{' '}
        {unansweredConfirmationCount === 1
          ? 'tournament needs confirmation replies'
          : 'tournaments need confirmation replies'}
        {confirmationFilterActive ? <i aria-hidden="true">×</i> : null}
      </span>
    ) : null;
    return (
      <>
        <div className="unionTournamentsHeader unionTournamentsPageHeader" data-help-anchor="union-tournaments-header">
          <h1>Tournaments</h1>
          <nav className="unionTournamentsTabs" aria-label="Tournament lists" data-help-anchor="union-tournaments-tabs">
            <button
              type="button"
              className={activeSection === 'upcoming' ? 'isActive' : ''}
              onClick={() => history.push('/tournaments/upcoming')}
            >
              Upcoming {typeof upcomingCount === 'number' ? <span>{upcomingCount}</span> : null}
            </button>
            <button
              type="button"
              className={activeSection === 'archive' ? 'isActive' : ''}
              onClick={() => history.push('/tournaments/archive')}
            >
              Past {typeof archiveCount === 'number' ? <span>{archiveCount}</span> : null}
            </button>
            <button
              type="button"
              className={activeSection === 'invites' ? 'isActive' : ''}
              onClick={() => history.push('/tournaments/invites/outbox')}
            >
              Invites {typeof invitesCount === 'number' ? <span>{invitesCount}</span> : null}
            </button>
            <button type="button" className={activeSection === 'communications' ? 'isActive' : ''} onClick={() => history.push('/tournaments/communications')}>
              Communications
              {communicationsUnreadCount ? <span className="unionTournamentsUnreadCount">{communicationsUnreadCount}</span> : null}
            </button>
          </nav>
          <button
            type="button"
            className="unionTournamentsAdd"
            data-help-anchor="union-tournaments-add"
            onClick={onAddTournament}
          >
            + Add tournament
          </button>
        </div>
        {nextStartChip || confirmationChip || typeof upcomingCount === 'number' ? (
          <div className="unionTournamentsSummaryStrip" data-help-anchor="union-tournaments-summary">
            {typeof upcomingCount === 'number' ? (
              <button type="button" onClick={() => history.push('/tournaments/upcoming')}>
                <b>{upcomingCount}</b> upcoming
              </button>
            ) : null}
            {nextStartChip}
            {confirmationChip}
            <span className="unionTournamentsSummaryContext">{this.props.user.activeSchoolName || 'Union'}</span>
          </div>
        ) : null}
      </>
    );
  }
}
